import { create } from "zustand";

export type PanelEntityType =
  | "rental"
  | "vehicle"
  | "station"
  | "dock"
  | "user"
  | "reservation"
  | "transaction"
  | "city"
  | "lock"
  | "vehicleModel"
  // A non-entity list view (e.g. an overview card's "view all"). Its content
  // travels on `data`; drilling into a row pushes the real entity on top.
  | "attention";

export interface PanelEntry {
  entityType: PanelEntityType;
  entityId: string;
  label?: string;
  /** Opaque payload for non-entity panels like "attention". */
  data?: unknown;
}

interface PanelState {
  stack: PanelEntry[];
  isOpen: boolean;
  openPanel: (entry: PanelEntry) => void;
  pushPanel: (entry: PanelEntry) => void;
  popPanel: () => void;
  popToIndex: (index: number) => void;
  closePanel: () => void;
}

export const usePanelStore = create<PanelState>()((set) => ({
  stack: [],
  isOpen: false,

  openPanel: (entry) => set({ stack: [entry], isOpen: true }),

  pushPanel: (entry) =>
    set((state) => ({ stack: [...state.stack, entry], isOpen: true })),

  popPanel: () =>
    set((state) => {
      const newStack = state.stack.slice(0, -1);
      return newStack.length === 0
        ? { stack: [], isOpen: false }
        : { stack: newStack };
    }),

  popToIndex: (index) =>
    set((state) => {
      const newStack = state.stack.slice(0, index + 1);
      return newStack.length === 0
        ? { stack: [], isOpen: false }
        : { stack: newStack };
    }),

  closePanel: () => set({ stack: [], isOpen: false }),
}));

export const usePanelIsOpen = () => usePanelStore((s) => s.isOpen);
export const usePanelStack = () => usePanelStore((s) => s.stack);
export const usePanelTopEntry = () =>
  usePanelStore((s) =>
    s.stack.length > 0 ? s.stack[s.stack.length - 1] : null
  );
